import React, { useState } from "react";
import {
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  FormHelperText,
  Paper,
  Typography,
  TextField,
  Alert,
  Button,
  FormControl,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import useRoleCategories from "../../../swr/hooks/Roles/useRoleCategories";
import API from "../../../axios/instances/API";
import { AMPLITUDE_KEYS, FEEDBACK_MESSAGES } from "../../../storage/constants";
import useAudienceOptions from "../../../swr/hooks/Audience/useAudienceOptions";
import * as amplitude from "@amplitude/analytics-browser";

export const Wrapper = styled(Paper)`
  padding: 30px;
  min-width: min(600px, 96vw);
  max-width: 600px;
  text-align: left;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  border: 2px solid black;
`;

const CHARACTER_LIMIT = 58;

const CreateRole = ({ setSnackbarText, setCreateModalOpen }) => {
  const { roleCategories, isLoading } = useRoleCategories();
  const { mutate } = useAudienceOptions();
  const [selectValue, setSelectValue] = useState("None");
  const [newRoleName, setNewRoleName] = useState("");
  const [newFrenchName, setNewFrenchName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorAlert, setErrorAlert] = useState("");
  const [charsMaxed, setCharsMaxed] = useState(false);
  const [frCharsMaxed, setFrCharsMaxed] = useState(false);
  const history = useHistory();

  if (isLoading) return null;

  const handleSubmit = async () => {
    setErrorAlert(false);
    setIsSubmitting(true);
    if (selectValue === "None") {
      setIsSubmitting(false);
      return setErrorAlert("You Must Choose a Category");
    }
    try {
      const { data } = await API.post("dashboard/roles/", {
        name: newRoleName,
        category_id: selectValue,
        fr_name: newFrenchName.length > 0 ? newFrenchName : newRoleName,
      });
      amplitude.track(AMPLITUDE_KEYS.CREATED_ROLE);
      mutate();
      setIsSubmitting(false);
      setSnackbarText(`Role "${newRoleName}" created success`);
      setCreateModalOpen(false);
      history.push(`/roles/details/${data.id}`);
    } catch (error) {
      setIsSubmitting(false);
      // Can't make role if there is already one with that name
      if (error.response && error.response.data) {
        return setErrorAlert(error.response.data.error);
      }
      return setErrorAlert(FEEDBACK_MESSAGES.GENERIC_ERROR);
    }
  };

  const handleUpdateRoleName = (e) => {
    const { value } = e?.target;

    const numChars = value?.length || 0;

    if (numChars > CHARACTER_LIMIT) {
      return setCharsMaxed(true);
    }

    setCharsMaxed(false);
    setNewRoleName(value);
  };

  const handleUpdateFrenchRoleName = (e) => {
    const { value } = e?.target;

    const numChars = value?.length || 0;

    if (numChars > CHARACTER_LIMIT) {
      return setFrCharsMaxed(true);
    }

    setFrCharsMaxed(false);
    setNewFrenchName(value);
  };

  const buttonDisabled =
    newRoleName.length < 1 ||
    selectValue === "None" ||
    isSubmitting ||
    charsMaxed ||
    frCharsMaxed;

  return (
    <Wrapper>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        <Typography variant="h3">Create Role</Typography>
      </div>
      <Typography variant="subtitle1" color="textPrimary" sx={{ mb: 6 }}>
        Fill in the inputs below to create a new Role.
      </Typography>
      <form onSubmit={handleSubmit}>
        <TextField
          variant="outlined"
          fullWidth
          autoFocus
          onChange={handleUpdateRoleName}
          value={newRoleName}
          error={charsMaxed}
          helperText={
            charsMaxed
              ? `${CHARACTER_LIMIT} characters maximum for role name`
              : "*Required"
          }
          label="New Role Name"
          size="small"
        />
        <div style={{ height: 10 }} />
        <div style={{ display: "flex", gap: 12 }}>
          <TextField
            variant="outlined"
            fullWidth
            value={newFrenchName}
            error={frCharsMaxed}
            helperText={
              frCharsMaxed &&
              `${CHARACTER_LIMIT} characters maximum for role name`
            }
            onChange={handleUpdateFrenchRoleName}
            label="French Role Name"
            size="small"
          />
          <div style={{ flexBasis: 200, flexGrow: 1 }}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="category_label">Category</InputLabel>
              <Select
                labelId="category_label"
                id="category_select"
                value={selectValue}
                onChange={(e) => setSelectValue(e.target.value)}
                label="Category"
              >
                <MenuItem value="None">
                  <em>None</em>
                </MenuItem>
                {roleCategories?.map((category) => (
                  <MenuItem key={category?.id} value={category?.id}>
                    {category?.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>*Required</FormHelperText>
            </FormControl>
          </div>
        </div>

        <Button
          disabled={buttonDisabled}
          type="submit"
          variant="contained"
          color="primary"
          sx={{ mt: 4 }}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={20} sx={{ color: "black" }} />
            ) : null
          }
          fullWidth
        >
          {isSubmitting ? "Creating" : "Create Role"}
        </Button>
      </form>

      {errorAlert && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          onClose={() => setErrorAlert("")}
        >
          {errorAlert}
        </Alert>
      )}
    </Wrapper>
  );
};

export default CreateRole;
